import React from "react";
import { useLocation } from "react-router-dom";
import { useApi } from "common/utils/api";
import { MobilicHeader } from "../common/Header";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { useLoadingScreen } from "common/utils/loading";
import {
  augmentAndSortMissions,
  parseMissionPayloadFromBackend
} from "common/utils/mission";
import { formatApiError, graphQLErrorMatchesCode } from "common/utils/errors";
import { useSnackbarAlerts } from "../common/Snackbar";
import { captureSentryException } from "common/utils/sentry";
import { useImpersonation } from "./utils/impersonation";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { UserReadTabs } from "./components/UserReadTabs";
import { orderEmployments } from "common/utils/employments";
import { UserReadInfo } from "./components/UserReadInfo";
import { UserReadHistory } from "./components/UserReadHistory";
import { TextWithBadge } from "../common/TextWithBadge";
import { UserReadAlerts } from "./components/UserReadAlerts";
import { getRegulationComputationsAndAlertNumber } from "common/utils/regulation/useGetUserRegulationComputationsByDay";
import { getAlertsGroupedByDayFromRegulationComputationsByDay } from "common/utils/regulation/groupAlertsByDay";
import { usePageTitle } from "../common/UsePageTitle";
import { USER_READ_TOKEN_QUERY } from "common/utils/apiQueries/auth";
import { USER_READ_QUERY } from "common/utils/apiQueries/user";
import { useWorkingDaysAnalysis } from "common/utils/hooks/useWorkingDaysAnalysis";
import { useTokenInfo } from "common/utils/hooks/useTokenInfo";

export function getTabs(alertNumber) {
  return [
    {
      name: "info",
      label: "Informations",
      icon: <InfoOutlinedIcon />,
      component: UserReadInfo
    },
    {
      name: "alerts",
      label: (
        <TextWithBadge
          badgeContent={alertNumber || 0}
          color={alertNumber ? "error" : "success"}
        >
          Infractions
        </TextWithBadge>
      ),
      icon: <WarningAmberOutlinedIcon />,
      component: UserReadAlerts
    },
    {
      name: "history",
      label: "Historique",
      icon: <HistoryOutlinedIcon />,
      component: UserReadHistory
    }
  ];
}

export function UserRead() {
  usePageTitle("Historique - Mobilic");
  const location = useLocation();
  const api = useApi();
  const withLoadingScreen = useLoadingScreen();
  const alerts = useSnackbarAlerts();

  const [userInfo, setUserInfo] = React.useState(null);
  const [tokenInfo, setTokenInfo] = React.useState(null);
  const [controlTime, setControlTime] = React.useState(null);
  const [missions, setMissions] = React.useState(null);
  const [employments, setEmployments] = React.useState([]);
  const [coworkers, setCoworkers] = React.useState(null);
  const [vehicles, setVehicles] = React.useState(null);
  const [periodOnFocus, setPeriodOnFocus] = React.useState(null);
  const [regulationComputationsByDay, setRegulationComputationsByDay] =
    React.useState([]);
  const [alertNumber, setAlertNumber] = React.useState(0);

  const [error, setError] = React.useState("");

  const impersonatingUser = useImpersonation(
    tokenInfo ? tokenInfo.token : null
  );

  // Transform token/control data into standardized tokenInfo format
  const effectiveTokenInfo = useTokenInfo({ tokenInfo, controlTime });

  React.useEffect(() => {
    const queryString = new URLSearchParams(location.search);
    const token = queryString.get("token");
    const ts = queryString.get("ts");

    if (token) {
      withLoadingScreen(async () => {
        try {
          const tokenResponse = await api.graphQlMutate(
            USER_READ_TOKEN_QUERY,
            {
              token
            },
            { context: { nonPublicApi: true } },
            true
          );
          setTokenInfo({
            ...tokenResponse.data.userReadToken,
            token
          });
        } catch (err) {
          captureSentryException(err);
          setError(
            formatApiError(err, (gqlError) => {
              if (graphQLErrorMatchesCode(gqlError, "INVALID_TOKEN")) {
                return "le lien d'accès à l'historique du salarié est invalide.";
              }
              if (graphQLErrorMatchesCode(gqlError, "EXPIRED_TOKEN")) {
                return "le lien d'accès à l'historique du salarié a expiré.";
              }
            })
          );
        }
      });
    }
    if (ts && ts !== "") setControlTime(ts);
  }, [location]);

  React.useEffect(() => {
    if (impersonatingUser) {
      withLoadingScreen(async () => {
        await alerts.withApiErrorHandling(async () => {
          const userResponse = await api.graphQlMutate(
            USER_READ_QUERY,
            null,
            null,
            true
          );
          const userPayload = userResponse.data.me;
          setUserInfo({
            id: userPayload.id,
            firstName: userPayload.firstName,
            lastName: userPayload.lastName,
            birthDate: userPayload.birthDate,
            email: userPayload.email
          });

          const resultMissions = userPayload.missions.edges.map((e) => e.node);

          const missionData = [];
          resultMissions.forEach((mission) => {
            const isMissionDeleted = !!mission.deletedAt;
            missionData.push({
              ...mission,
              ...parseMissionPayloadFromBackend(mission, userPayload.id),
              isDeleted: isMissionDeleted,
              allActivities: mission.activities.map((activity) => ({
                ...activity,
                isMissionDeleted
              }))
            });
          });

          const missions_ = augmentAndSortMissions(
            missionData,
            userPayload.id
          ).filter((m) => m.activities.length > 0);
          setMissions(missions_);

          const _vehicles = {};
          userPayload.employments.forEach((e) => {
            e.company.vehicles.forEach((v) => {
              _vehicles[v.id.toString()] = v;
            });
          });
          setVehicles(_vehicles);
          const _coworkers = {};
          userPayload.missions.edges.forEach((m) => {
            m.node.activities.forEach((a) => {
              _coworkers[a.user.id.toString()] = a.user;
            });
          });
          setCoworkers(_coworkers);
          setEmployments(orderEmployments(userPayload.employments));
        });
      });
    }
  }, [impersonatingUser]);

  React.useEffect(() => {
    if (!userInfo || !userInfo.id) {
      return;
    }
    const loadData = async () => {
      const res = await getRegulationComputationsAndAlertNumber(
        api,
        userInfo.id,
        new Date(effectiveTokenInfo.historyStartDay)
      );
      setRegulationComputationsByDay(res.regulationComputationsByDay);
      setAlertNumber(res.alertNumber);
    };
    loadData();
  }, [userInfo, effectiveTokenInfo]);

  const groupedAlerts = React.useMemo(
    () =>
      getAlertsGroupedByDayFromRegulationComputationsByDay(
        regulationComputationsByDay
      ),
    [regulationComputationsByDay]
  );

  // Analyze working days: detect days added posteriori and modified days
  const workingDaysAnalysis = useWorkingDaysAnalysis(
    missions,
    controlTime,
    effectiveTokenInfo
  );

  const TABS = getTabs(alertNumber);

  return [
    <MobilicHeader key={0} disableMenu />,
    error ? (
      <Container key={1}>
        <Typography align="center" color="error">
          Impossible d'accéder à la page : {error}
        </Typography>
      </Container>
    ) : missions ? (
      <UserReadTabs
        key={1}
        tabs={TABS}
        userInfo={userInfo}
        tokenInfo={effectiveTokenInfo}
        controlTime={controlTime}
        missions={missions}
        employments={employments}
        coworkers={coworkers}
        vehicles={vehicles}
        periodOnFocus={periodOnFocus}
        setPeriodOnFocus={setPeriodOnFocus}
        workingDaysNumber={workingDaysAnalysis.workingDaysNumber}
        daysAddedPosterioriNumber={workingDaysAnalysis.daysAddedPosterioriNumber}
        daysModifiedNumber={workingDaysAnalysis.daysModifiedNumber}
        groupedAlerts={groupedAlerts}
        readOnlyAlerts={true}
      />
    ) : null
  ];
}
